import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Api from '../../libs/Api';
import translate from '../../libs/i18n';

import { RequestPasswordStyle } from './RequestPasswordStyle';
import emailRegex from '../../utils/emailRegex';

import InputField from '../../components/InputField/InputField';
import Loading from '../../components/Loading/Loading';
import SubmitButton from '../../components/SubmitButton/SubmitButton';

import EmailIcon from '../../assets/svgs/EmailIcon';
import Logo from '../../assets/svgs/Logo';
import PadlockIcon from '../../assets/svgs/PadlockIcon';

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const RequestPassword = () => {
  const language = useSelector((state) => state.settings.language);
  const query = useQuery();
  const token = query.get('token');
  const user = useSelector((state) => state.meUser);
  const history = useHistory(); 

  const hasUser = user && Object.keys(user).length > 0;
  const hasToken = !!token;

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');


const verifyEmail = (value) => {
  if (emailRegex.test(String(value).toLowerCase())) {
    return true;
  }

  setEmailError(translate('invalidEmail', language));

  return false;
};

const verifyPassword = (value, repeatValue) => {
  if (value.length && repeatValue.length) {

    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(value);
    const hasLowerCase = /[a-z]/.test(value);
    const hasNumber = /\d/.test(value);
    const hasSpecialChar = /[!@#$%^&*.()]/.test(value);
    const isValidLength = value.length >= minLength;
  
    if (!isValidLength) {
      setPasswordError(translate('passwordMinLength', language));
      return false;
    }
    if (!hasUpperCase) {
      setPasswordError(translate('passwordUppercase', language));
      return false;
    }
    if (!hasLowerCase) {
      setPasswordError(translate('passwordLowercase', language));
      return false;
    }
    if (!hasNumber) {
      setPasswordError(translate('passwordNumber', language));
      return false;
    }
    if (!hasSpecialChar) 
    {
      setPasswordError(translate('passwordSpecialChar', language))
      return false;
    }

    if (value === repeatValue) {
      return true;
    }

    setPasswordError(translate('passwordsDoNotMatch', language));

    return false;
  }

  setPasswordError(translate('passwordRequired', language));

  return false;
};

const handleRequestSubmit = async (event) => {
  event.preventDefault();
  setEmailError('');
  setSuccessMessage('');
  setErrorMessage('');

  if (verifyEmail(email)) {
    try {
      const response = await Api.postRequestPasswordReset(email);

      if (response.status === 200) {
        setSuccessMessage(translate('requestSent', language));
      } else if (response.status === 400) {
        setErrorMessage(translate('emailNotRegistered', language));
      } else {
        setErrorMessage(translate('emailError', language));
      }
    } catch (error) {
      setErrorMessage(translate('emailError', language));
    }
  } else {
    setEmailError(translate('emailInvalid', language));
  }
};


const handleResetSubmit = async (event) => {
  event.preventDefault();
  setPasswordError('');
  setSuccessMessage(''); 
  setErrorMessage('');

  if (verifyPassword(newPassword, confirmPassword)) {
    try {
      let response;

      if (token) {
        response = await Api.postConfirmPasswordReset({
          token: token,
          new_password: newPassword,
        });
      } else {
        response = await Api.postConfirmPasswordReset({
          user: user,
          new_password: newPassword,
        });
      }

      if (response) {
        setSuccessMessage(translate('passwordReset', language));
        setTimeout(() => {
          history.push('/entrar');
        }, 2000);
      } else {
        setErrorMessage(translate('errorPasswordReset', language));
      }
    } catch (error) {
      setErrorMessage(translate('errorPasswordReset', language));
    }
  }
};


return (
  <RequestPasswordStyle>
    <Header />
    
    {!hasUser && !hasToken ? (
      <>
        <h1 className="request-password-title">{translate('requestPassword', language)}</h1>
        <p className="request-password-description">
          {translate('requestPasswordInstructions', language)}
        </p>
        <form onSubmit={handleRequestSubmit}>
          <div className="form-content">
            <InputField
                    type="text"
                    placeholder={translate('typeYourEmail', language)}
                    inputName="email"
                    value={email}
                    icon={<EmailIcon />}
                    error={emailError}
                    onChange={setEmail}
                    required={true}
              />

            <SubmitButton value={translate('requestReset', language)} onClick={handleRequestSubmit} />

            {successMessage && <p className="success-message">{successMessage}</p>}
            {errorMessage && <p className="error-message">{errorMessage}</p>}
          </div>
        </form>
      </>
    ) : (
      <>
        <h1 className="reset-password-title">{translate('resetPassword', language)}</h1>
        
        <form onSubmit={handleResetSubmit}>
          <div className="form-content">
            <InputField
              type="password"
              placeholder={translate('newPassword', language)}
              inputName="password"
              value={newPassword}
              icon={<PadlockIcon />}
              theme={passwordError.length ? 'error' : ''}
              instructions={translate('passwordInstructions', language)}
              onChange={setNewPassword}
            />
            <InputField
              type="password"
              placeholder={translate('confirmNewPassword', language)}
              inputName="confirm-password"
              value={confirmPassword}
              icon={<PadlockIcon />}
              error={passwordError.length ? passwordError : ''}
              onChange={setConfirmPassword}
            />

            <div className="buttons-row">
              <SubmitButton
                theme="cancel"
                type="button"
                value={translate('cancel', language)}
                onClick={() => history.goBack()}
              />

              <SubmitButton
                value={translate('resetPassword', language)}
                onClick={handleResetSubmit}
              />
            </div>

            {successMessage && <p className="success-message">{successMessage}</p>}
            {errorMessage && <p className="error-message">{errorMessage}</p>}
          </div>
        </form>
      </>
    )}
  </RequestPasswordStyle>
);
};

export default RequestPassword;

